import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import password_on from 'assets/icons/password-on.svg';
import password_off from 'assets/icons/password-off.svg';

import { mfaChangeInitialPassword } from 'config/axiosInstance';
import PasswordStrength from '../ForgotPassword/passwordStrength';
import PasswordRequirements, { usePasswordRuleError } from '../PasswordRequirements';
import { normalizePassword, passwordsMatch } from 'config/passwordRules';
import { ErrorBox, MfaFrame, MfaStepProps, SubmitButton, useAttempt, useStepFailure } from './MfaFrame';

/**
 * 初めてのログイン（mfa が password_change_required）。招待メールの初期パスワードを、新しいパスワードに変える。
 * 強さの目安と確認の欄は、パスワードの再設定（newPassword.tsx）と同じ。
 * EV-474（2026-10-26）。送れるかどうかは、サーバー・アプリと同じ規則（config/passwordRules）で判定し、足りない条件を
 * 名前で出す（以前は強さの点数で判定し、サーバーと食い違っていた）。辞書・利用者の情報が要る規則はサーバー
 * （validate_password）が行い、断られたらその文を出す。前後の空白・改行は取り除いて比べ、取り除いた値を送る（#402 と同じ）。
 */
const PasswordChangeStep: React.FC<MfaStepProps> = ({ challenge, onNext, onRestart }) => {
  const { t } = useTranslation('login');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const failure = useStepFailure(onRestart);
  const beginAttempt = useAttempt();
  const ruleError = usePasswordRuleError();
  const requirementsId = useId();

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!password || !confirm) {
      failure.setText(t('set-new-password.error-password-strength'));
      return;
    }
    if (!passwordsMatch(password, confirm)) {
      failure.setText(t('set-new-password.error-password-match'));
      return;
    }
    const unmet = ruleError(password);
    if (unmet) {
      failure.setText(unmet);
      return;
    }
    setBusy(true);
    failure.setText('');
    const signal = beginAttempt();
    try {
      const next = await mfaChangeInitialPassword(challenge.token, normalizePassword(password), { signal });
      if (!signal.aborted) onNext(next);
    } catch (error) {
      if (signal.aborted) return;
      failure.handle(error);
      setBusy(false);
    }
  };

  const errorId = failure.text ? 'mfa-password-error' : undefined;
  return (
    <MfaFrame
      title={t('mfa.password-title')}
      subtitle={t('mfa.password-subtitle')}
      onBack={() => onRestart(null)}
      backDisabled={busy}
    >
      <form onSubmit={submit} className="flex flex-col gap-6" noValidate>
        <div className="w-full">
          <label htmlFor="mfa-new-password" className="block mb-2 text-sm font-regular text-neutral/100">
            {t('set-new-password.password')}
          </label>
          <div className="flex items-center relative mb-2">
            <input
              type={show ? 'text' : 'password'}
              id="mfa-new-password"
              autoComplete="new-password"
              className="w-full border border-neutral/50 text-neutral/300 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 pl-4 pr-12"
              placeholder={t('set-new-password.password-holder')}
              value={password}
              aria-describedby={[errorId, requirementsId].filter(Boolean).join(' ')}
              onChange={(e) => {
                setPassword(e.target.value);
                failure.setText('');
              }}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-4"
              onClick={() => setShow(!show)}
              aria-label={t(show ? 'ui.hide-password' : 'ui.show-password', { ns: 'common' })}
              aria-pressed={show}
              aria-controls="mfa-new-password"
            >
              <img src={show ? password_off : password_on} alt="" />
            </button>
          </div>
          <PasswordStrength password={password} />
        </div>
        <div className="w-full">
          <label htmlFor="mfa-confirm-password" className="block mb-2 text-sm font-regular text-neutral/100">
            {t('set-new-password.re-password')}
          </label>
          <div className="flex items-center relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              id="mfa-confirm-password"
              autoComplete="new-password"
              className="w-full border border-neutral/50 text-neutral/300 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 pl-4 pr-12"
              placeholder={t('set-new-password.password-holder')}
              value={confirm}
              aria-describedby={errorId}
              onChange={(e) => {
                setConfirm(e.target.value);
                failure.setText('');
              }}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-4"
              onClick={() => setShowConfirm(!showConfirm)}
              aria-label={t(showConfirm ? 'ui.hide-password' : 'ui.show-password', { ns: 'common' })}
              aria-pressed={showConfirm}
              aria-controls="mfa-confirm-password"
            >
              <img src={showConfirm ? password_off : password_on} alt="" />
            </button>
          </div>
        </div>
        <PasswordRequirements id={requirementsId} password={password} confirm={confirm} />
        <ErrorBox id="mfa-password-error" text={failure.text} />
        <SubmitButton busy={busy} disabled={failure.waiting} label={t('mfa.password-button')} />
      </form>
    </MfaFrame>
  );
};

export default PasswordChangeStep;
